import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from 'src/utils/firebase';
import Swal from 'sweetalert2';
import axios from 'axios';
import { checkResponse, getLearnworldsHeaders } from 'src/utils/headers_stuff';

type ClientOption = {
  schoolId: string;
  email: string;
  collegeName: string;
  contactName: string;
};

export default function TeacherEnrollmentPage() {
  const [teacherName, setTeacherName] = useState('');
  const [college, setCollege] = useState('');
  const [otherCollege, setOtherCollege] = useState('');
  const [email, setEmail] = useState('');
  const [skus, setSkus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [apiSuccessMessage, setSuccessMessage] = useState('');

  const [all_course_selections, setCourseSelection] = useState<any[] | null>(
    null
  );
  const [selectedCourse, setSelectedCourse] = useState('');

  const resultMapping = [
    "Created customer in Shopify and added 'teacher' tag", // 0
    'Invited customer in Shopify', // 1
    "Modified existing customer in Shopify and added 'teacher' tag", // 2
    "Found existing customer in Shopify with 'teacher' tag", // 3
    'Created new LWs user', // 4
    'Found existing LWs user', // 5
    'Enrolled user into course', // 6
    'User was already enrolled into a given course' // 7
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      Swal.fire({
        title: 'Loading in details',
        text: 'Please wait while all courses are being pulled.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        const mainCollectionQuery = query(
          collection(db, 'orders'),
          where('auth_email', '!=', null)
        );

        const snapshot = await getDocs(mainCollectionQuery);
        const possibleCollegeList: ClientOption[] = [];

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();

          if (data.schoolID) {
            if (data.auth_email) {
              possibleCollegeList.push({
                schoolId: data.schoolID,
                email: data.auth_email,
                collegeName: data.name,
                contactName: data.academic_contact_name
              });
            }
          }
        });

        setClientOptions(possibleCollegeList);

        if (!all_course_selections) {
          const all_titles = await getAllTitles();
          const filtered_titles = (all_titles ?? []).map((work) => ({
            id: work.id,
            title: work.title
          }));
          setCourseSelection(
            [...filtered_titles]
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((work) => ({
                id: work.id,
                title: work.title
              }))
          );
        }
      } catch (err) {
        console.error('Error fetching colleges:', err);
      }
      Swal.close();
    };

    fetchUsers();
  }, []);

  async function getAllTitles() {
    const baseUrl = 'https://learnnow.learnworlds.com/admin/api/';
    const endpoint = '/v2/courses';
    const learnworldsHeaders = await getLearnworldsHeaders();
    let allCourses = [];
    let page = 1;
    let totalPages = 1;
    while (page <= totalPages) {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        access: 'private'
      });
      const url = `${baseUrl}${endpoint}?${queryParams}`;
      const response = await fetch(url, {
        headers: learnworldsHeaders
      });
      await checkResponse(
        response,
        'GET',
        url,
        learnworldsHeaders,
        'getAllTitles'
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch courses. Status: ${response.status}`);
      }
      const responseData = await response.json();
      totalPages = responseData.meta.totalPages;
      allCourses = allCourses.concat(responseData.data);
      page++;
    }
    // Sort allCourses alphabetically by title
    allCourses.sort((a, b) => {
      const titleA = a.title.toUpperCase(); // Convert titles to uppercase for case-insensitive comparison
      const titleB = b.title.toUpperCase();
      // Handle potential inconsistencies in titles
      if (titleA < titleB) {
        return -1;
      } else if (titleA > titleB) {
        return 1;
      } else {
        // If titles are equal, sort by ID to ensure deterministic ordering
        return a.id.localeCompare(b.id);
      }
    });
    return allCourses;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSuccessMessage('');

    Swal.fire({
      title: 'Creating new user...',
      text: 'Please wait while new user is being created.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    if (!teacherName || !college || !email) {
      setError('Please complete all required fields.');
      return;
    }

    const resolvedCollege = college === 'Other' ? otherCollege : college;

    if (college === 'Other' && !otherCollege) {
      setError('Please specify the college.');
      Swal.close();
      return;
    }

    const skuSet = skus
      ? skus
          .split(', ')
          .map((sku) => sku.trim())
          .filter((sku) => sku.length > 0)
      : [];
    const uniqueSkuSet = skuSet.filter(
      (sku, index, arr) => arr.indexOf(sku) === index
    );

    const payload = {
      teacherName,
      teacherCollege: resolvedCollege,
      teacherEmail: email,
      skus: uniqueSkuSet
    };

    try {
      setLoading(true);

      const fuser = auth.currentUser;
      if (!fuser) {
        throw new Error('Not logged in');
      }

      const token = await fuser.getIdToken();

      const response = await axios.post(
        'https://us-central1-lnp-orderhandling-app.cloudfunctions.net/TeacherEnrollmentFunction',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status !== 200) {
        await Swal.fire({
          position: 'center',
          icon: 'error',
          title: `Failed to enrol teacher!`,
          showConfirmButton: false
        });
        return;
      }

      setSuccess(true);
      // setTeacherName('');
      // setCollege('');
      // setOtherCollege('');
      // setEmail('');
      // setSkus('');

      const mappedMessages = response.data.message
        .map((x: number) => resultMapping[x])
        .filter(Boolean);

      setSuccessMessage(mappedMessages.join('<br>'));

      await Swal.fire({
        position: 'center',
        icon: 'info',
        title: 'Teacher created!',
        showConfirmButton: true
      });
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      Swal.close();
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    const selected = clientOptions.find(
      (option) => option.email.toLowerCase() === value.toLowerCase()
    );

    if (selected) {
      setCollege(selected.schoolId);
      if (selected.contactName) {
        setTeacherName(selected.contactName);
      }
    }

    setEmail(value);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Teacher Enrolment
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Teacher's Email"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            margin="normal"
          />

          <TextField
            fullWidth
            label="Teacher's Name"
            value={teacherName}
            onChange={(e) => setTeacherName(e.target.value)}
            required
            margin="normal"
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="college-select-label">Teacher's College</InputLabel>
            <Select
              labelId="college-select-label"
              value={college}
              label="Teacher's College"
              onChange={(e) => setCollege(e.target.value)}
            >
              {clientOptions.map((option) => (
                <MenuItem
                  key={`${option.schoolId}-${option.email}`}
                  value={option.schoolId}
                >
                  {option.collegeName} ({option.schoolId})
                </MenuItem>
              ))}
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {college === 'Other' && (
            <TextField
              fullWidth
              label="Specify College"
              value={otherCollege}
              onChange={(e) => setOtherCollege(e.target.value)}
              required
              margin="normal"
            />
          )}

          <Autocomplete
            options={all_course_selections || []}
            getOptionLabel={(option) => `${option.id} ${option.title}`}
            value={
              all_course_selections?.find((c) => c.id === selectedCourse) ||
              null
            }
            onChange={(event, newValue) => {
              if (!newValue) return;

              const selectedSku = newValue.id;

              setSelectedCourse(selectedSku);

              setSkus((prev: string): string => {
                const skuList = prev ? prev.split(', ') : [];
                skuList.push(selectedSku);

                const uniqueSkus = skuList.filter(
                  (sku, index, arr) => arr.indexOf(sku) === index
                );

                return uniqueSkus.join(', ');
              });
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                  {option.id}
                </Box>
                {option.title}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Course"
                margin="normal"
                fullWidth
              />
            )}
          />

          <TextField
            fullWidth
            label="Instant Enrolment to SKU(s) (optional)"
            placeholder="Comma-separated SKUs"
            value={skus}
            onChange={(e) => setSkus(e.target.value)}
            margin="normal"
          />

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {success && (
            <Typography color="success.main" sx={{ mt: 2 }}>
              {apiSuccessMessage.split('<br>').map((line, i) => (
                <span key={i}>
                  - {line}
                  <br />
                </span>
              ))}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
